// src/components/ReelsFeed.js
// Immersive, Instagram-Reels-style vertical feed: one full-screen reel at a
// time, vertical scroll-snap between them, autoplay-on-view (and pause the
// rest), tap-to-play/pause, double-tap-to-like, a right-side action rail
// (like / comment-count / share / follow) and a caption + creator overlay.
//
// Unlike PromoViewer (a timed Stories player that auto-advances), this is a
// true swipe/scroll feed — the video plays for as long as the viewer stays on
// it and loops, exactly like Reels.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { B } from '../constants';
import { promos as promosAPI, users as usersAPI } from '../api';
import { trackEvent as trackFeedEvent } from '../lib/feedEvents';
import { trackActivity } from '../lib/activityEvents';
import PlatformShareModal from './PlatformShareModal';
import PromoCommentDrawer from './PromoCommentDrawer';
import ConfirmModal from './ConfirmModal';
import { isDirectVideo, isYouTube } from '../lib/videoUrl';
import YouTubeEmbed from './YouTubeEmbed';
import { reelCap, loadSeen, saveSeen, isReelLocked } from '../lib/reelWall';

// Shared empty default so the seeding effects below don't see a brand-new array
// on every render (which re-ran them forever).
const EMPTY = [];

function compactNumber(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(v);
}

// A single action-rail button (icon + count), stacked vertically like IG/TikTok.
function RailButton({ icon, label, onClick, active, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={typeof label === 'string' ? label : undefined}
      style={{
        background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 0,
      }}
    >
      <span style={{
        fontSize: 30, lineHeight: 1, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
        transition: 'transform 0.18s ease', transform: active ? 'scale(1.18)' : 'scale(1)',
      }}>{icon}</span>
      {label != null && (
        <span style={{ color: 'white', fontSize: 12, fontWeight: 700, fontFamily: "'Montserrat',sans-serif", textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
          {label}
        </span>
      )}
    </button>
  );
}

function Slide({
  promo, index, activeIndex, muted, user, followed, autoplayBlocked,
  liked, likeCount, viewCount, timeLeft, onToggleMute, onLike, onShare, onComment, onFollow, onDelete,
  onArchive, onPlaybackResumed, registerVideo, onPlanTrip, commentCount, locked, onUnlock,
}) {
  const isOwner = Boolean(user && promo.user?.id === user.id);
  const isActive = index === activeIndex;
  // Only mount <video> for the active slide and its immediate neighbours so we
  // never have dozens of decoding video elements at once.
  const shouldMountVideo = !locked && Math.abs(index - activeIndex) <= 1;
  const yt = isYouTube(promo.videoUrl);

  const [paused, setPaused] = useState(false);
  const [burst, setBurst] = useState(false);
  const [vProgress, setVProgress] = useState(0);
  // Video URLs are short-lived presigned links, so a reel opened long enough
  // (or a transient network failure) fails to load with nothing but a black
  // frame. Keep the playable src separately so it can be re-signed and retried.
  const [src, setSrc] = useState(promo.videoUrl);
  // A null videoUrl means the server couldn't sign the upload; offer a retry.
  const [failed, setFailed] = useState(!promo.videoUrl && promo.videoAvailable !== false);
  // The server HEADs the object when it signs the URL; `videoAvailable: false`
  // means the file itself is gone, which no amount of re-signing will fix.
  const [gone, setGone] = useState(promo.videoAvailable === false);
  const [retrying, setRetrying] = useState(false);
  const resignedRef = useRef(false);
  const videoRef = useRef(null);
  const tapRef = useRef({ t: 0, timer: null });
  const direct = isDirectVideo(src) && !gone;

  useEffect(() => {
    setSrc(promo.videoUrl);
    setFailed(false);
    setGone(promo.videoAvailable === false);
    resignedRef.current = false;
  }, [promo.videoUrl, promo.videoAvailable]);

  useEffect(() => { registerVideo(index, videoRef.current); }, [index, registerVideo, shouldMountVideo]);

  // Re-sign through GET /promos/:id (side-effect free) and swap the src in.
  const retry = useCallback(async () => {
    setRetrying(true);
    try {
      const fresh = await promosAPI.get(promo.id);
      if (fresh?.videoAvailable === false) {
        setGone(true);
        setFailed(false);
        return false;
      }
      if (fresh?.videoUrl) {
        setSrc(fresh.videoUrl);
        setFailed(false);
        return true;
      }
      setFailed(true);
      return false;
    } catch {
      setFailed(true);
      return false;
    } finally {
      setRetrying(false);
    }
  }, [promo.id]);

  const handleError = () => {
    if (!resignedRef.current) {
      resignedRef.current = true;
      retry();
      return;
    }
    setFailed(true);
  };

  // Keep the paused overlay in sync when this slide loses/gains focus.
  useEffect(() => { if (!isActive) setPaused(false); }, [isActive]);

  const triggerBurst = () => {
    setBurst(true);
    setTimeout(() => setBurst(false), 750);
  };

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      Promise.resolve(el.play()).then(() => onPlaybackResumed?.()).catch(() => {});
      setPaused(false);
    }
    else { el.pause(); setPaused(true); }
  };

  // Single tap → play/pause; double tap → like (with heart burst), IG-style.
  const handleTap = () => {
    const now = Date.now();
    if (now - tapRef.current.t < 280) {
      clearTimeout(tapRef.current.timer);
      tapRef.current.t = 0;
      triggerBurst();
      onLike(promo, { force: true });
    } else {
      tapRef.current.t = now;
      tapRef.current.timer = setTimeout(() => { togglePlay(); tapRef.current.t = 0; }, 280);
    }
  };

  const handleTimeUpdate = (e) => {
    const el = e.currentTarget;
    if (el.duration > 0) setVProgress((el.currentTime / el.duration) * 100);
  };

  return (
    <div
      data-index={index}
      style={{
        position: 'relative', width: '100%', height: '100%',
        scrollSnapAlign: 'start', scrollSnapStop: 'always',
        background: '#000', overflow: 'hidden', flex: '0 0 100%',
      }}
    >
      {/* Media */}
      <div style={{ position: 'absolute', inset: 0 }} onClick={handleTap}>
        {shouldMountVideo && direct ? (
          <video
            ref={videoRef}
            src={src || undefined}
            loop muted={muted} playsInline
            preload={isActive ? 'auto' : 'metadata'}
            poster={promo.thumbnailUrl || undefined}
            onTimeUpdate={handleTimeUpdate}
            onError={handleError}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : shouldMountVideo && yt ? (
          <YouTubeEmbed
            videoUrl={promo.videoUrl}
            title={promo.title}
            autoplay={isActive}
            muted={muted || !isActive}
            style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
          />
        ) : (
          // Poster for off-screen slides (and non-video promos).
          <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: `linear-gradient(135deg,${B.night},#1a3a2a)` }}>
            {promo.thumbnailUrl && (
              <img src={promo.thumbnailUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: locked ? 'blur(18px)' : undefined, transform: locked ? 'scale(1.1)' : undefined }} />
            )}
          </div>
        )}

        {/* Readability gradient */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0) 38%, rgba(0,0,0,0) 78%, rgba(0,0,0,0.35) 100%)', pointerEvents: 'none' }} />

        {locked && (
          <div data-testid="reel-wall" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', zIndex: 9 }}>
            <div style={{ textAlign: 'center', color: 'white', fontFamily: "'Montserrat',sans-serif", padding: 28, maxWidth: 320 }}>
              {user ? (
                <>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🌅</div>
                  <p style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>You've seen today's picks</p>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>Fresh reels land tomorrow. Time to plan the trip these inspired?</p>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🔒</div>
                  <p style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>Register with GreatBreak to keep watching</p>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginBottom: 16 }}>Free account, more reels, and trips planned from the ones you love.</p>
                  {onUnlock && (
                    <button onClick={(e) => { e.stopPropagation(); onUnlock(); }}
                      style={{ background: B.amber, color: B.night, border: 'none', borderRadius: 22, padding: '10px 22px', fontSize: 13, fontWeight: 800, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
                      Register / Sign in
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Playback failure: a black frame is indistinguishable from a stall,
            so say what happened and offer an explicit retry. */}
        {isActive && gone && (
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.6)', zIndex: 8 }}>
            <div style={{ textAlign: 'center', color: 'white', fontFamily: "'Montserrat',sans-serif", padding: 24 }}>
              <div style={{ fontSize: 34, marginBottom: 10 }}>🎬</div>
              <p style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>This video is no longer available</p>
              <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)' }}>Swipe to keep watching.</p>
            </div>
          </div>
        )}

        {isActive && failed && !gone && (
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.6)', zIndex: 8 }}>
            <div style={{ textAlign: 'center', color: 'white', fontFamily: "'Montserrat',sans-serif", padding: 24 }}>
              <div style={{ fontSize: 34, marginBottom: 10 }}>⚠️</div>
              <p style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>This reel wouldn’t load</p>
              <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', marginBottom: 14 }}>Check your connection and try again.</p>
              <button
                onClick={(e) => { e.stopPropagation(); retry(); }}
                disabled={retrying}
                style={{ background: B.amber, color: B.night, border: 'none', borderRadius: 22, padding: '9px 20px', fontSize: 13, fontWeight: 800, cursor: retrying ? 'default' : 'pointer', fontFamily: "'Montserrat',sans-serif" }}
              >{retrying ? 'Retrying…' : 'Retry'}</button>
            </div>
          </div>
        )}

        {/* Autoplay refused by the browser (common on iOS with sound on) —
            surface the tap that starts it instead of showing a frozen poster. */}
        {isActive && autoplayBlocked && !failed && !gone && (
          <div style={{ position: 'absolute', bottom: 108, left: '50%', transform: 'translateX(-50%)', zIndex: 8, background: 'rgba(0,0,0,0.6)', color: 'white', borderRadius: 22, padding: '8px 16px', fontSize: 12.5, fontWeight: 700, fontFamily: "'Montserrat',sans-serif", pointerEvents: 'none' }}>
            ▶ Tap to play
          </div>
        )}

        {/* Paused indicator */}
        {isActive && paused && !failed && !gone && (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 76, height: 76, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z" /></svg>
          </div>
        )}

        {/* Double-tap heart burst */}
        {burst && (
          <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 110, pointerEvents: 'none', animation: 'gbReelBurst 0.75s ease-out forwards' }}>❤️</span>
        )}
      </div>

      {/* Per-video progress bar */}
      {direct && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'rgba(255,255,255,0.2)', zIndex: 6 }}>
          <div style={{ height: '100%', width: `${isActive ? vProgress : 0}%`, background: 'white' }} />
        </div>
      )}

      {/* Top-right controls: mute, plus delete for the reel's own creator */}
      <div style={{ position: 'absolute', top: 18, right: 16, zIndex: 7, display: 'flex', gap: 8 }}>
        {isOwner && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); onArchive(promo); }}
              aria-label="Archive reel"
              style={{ background: 'rgba(0,0,0,0.45)', border: 'none', color: 'white', width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', fontSize: 16, display: 'grid', placeItems: 'center' }}
            >📥</button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(promo); }}
              aria-label="Delete reel"
              style={{ background: 'rgba(0,0,0,0.45)', border: 'none', color: 'white', width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', fontSize: 16, display: 'grid', placeItems: 'center' }}
            >🗑</button>
          </>
        )}
        {(direct || yt) && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleMute(); }}
            aria-label={muted ? 'Unmute' : 'Mute'}
            style={{ background: 'rgba(0,0,0,0.45)', border: 'none', color: 'white', width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', fontSize: 17, display: 'grid', placeItems: 'center' }}
          >{muted ? '🔇' : '🔊'}</button>
        )}
      </div>

      {/* Right action rail */}
      <div style={{ position: 'absolute', right: 12, bottom: 96, zIndex: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22 }}>
        {/* Creator avatar + follow */}
        <div style={{ position: 'relative', marginBottom: 6 }}>
          <div style={{ width: 46, height: 46, borderRadius: '50%', border: '2px solid white', overflow: 'hidden', background: `linear-gradient(135deg,${B.forest},${B.mint})`, display: 'grid', placeItems: 'center' }}>
            {promo.user?.avatar
              ? <img src={promo.user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ color: 'white', fontWeight: 800, fontSize: 16 }}>{promo.user?.name?.[0] || '?'}</span>}
          </div>
          {promo.user?.id && !isOwner && !followed && (
            <button
              onClick={(e) => { e.stopPropagation(); onFollow(promo.user.id); }}
              aria-label={`Follow ${promo.user?.name || 'creator'}`}
              style={{ position: 'absolute', bottom: -12, left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', borderRadius: 12, border: '1.5px solid white', background: B.amber, color: B.night, fontSize: 10, fontWeight: 800, padding: '2px 8px', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}
            >Follow me</button>
          )}
        </div>

        <RailButton icon={liked ? '❤️' : '🤍'} active={liked} label={compactNumber(likeCount)} onClick={() => onLike(promo)} />
        <RailButton icon="💬" label={compactNumber(commentCount)} onClick={() => onComment(promo)} />
        <RailButton icon="↗️" label="Share" onClick={() => onShare(promo)} />
        <RailButton icon="👁️" label={compactNumber(viewCount)} disabled />
      </div>

      {/* Bottom caption / creator info */}
      <div style={{ position: 'absolute', left: 16, right: 78, bottom: 26, zIndex: 6, color: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontWeight: 800, fontSize: 15, fontFamily: "'Montserrat',sans-serif", textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
            @{promo.user?.username || promo.user?.name || 'traveller'}
          </span>
          {promo.user?.isInfluencer && (
            <span style={{ background: `linear-gradient(135deg,${B.amber},#ff6b35)`, color: 'white', fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 10, letterSpacing: 0.5 }}>✨ CREATOR</span>
          )}
        </div>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 19, fontWeight: 700, marginBottom: 4, textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>{promo.title}</h3>
        {promo.caption && (
          <p style={{ fontSize: 13.5, lineHeight: 1.45, color: 'rgba(255,255,255,0.9)', fontFamily: "'Montserrat',sans-serif", textShadow: '0 1px 4px rgba(0,0,0,0.6)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{promo.caption}</p>
        )}
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {promo.destination && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,0.4)', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>
              <span>📍</span><span style={{ color: B.mint }}>{promo.destination}</span>
            </div>
          )}
          {promo.destination && onPlanTrip && (
            <button type="button" onClick={(e) => { e.stopPropagation(); trackActivity('booking', 'booking_click', { promoId: promo.id }); onPlanTrip(promo); }}
              aria-label={`Plan a trip to ${promo.destination}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: B.forest, color: '#fff', border: 'none', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 700, fontFamily: "'Montserrat',sans-serif", cursor: 'pointer' }}>
              ✈️ Plan this trip
            </button>
          )}
          {isOwner && timeLeft && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,0.4)', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>
              <span>⏱</span><span>{timeLeft}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReelsFeed({ promos = EMPTY, startIndex = 0, onClose, user, onAuthRequired, followingIds = EMPTY, onFollowUpdate, onDeleted, onPlanTrip }) {
  const [activeIndex, setActiveIndex] = useState(startIndex);
  const [muted, setMuted] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('gb_promo_muted') !== '0';
  });
  const [liked, setLiked] = useState({});
  const [likes, setLikes] = useState({});
  const [views, setViews] = useState({});
  const [followed, setFollowed] = useState(() => new Set(followingIds));
  const [shareFor, setShareFor] = useState(null);
  const [commentFor, setCommentFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [archiveFor, setArchiveFor] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [commentCounts, setCommentCounts] = useState({});
  const cap = reelCap(user);
  const [seenIds, setSeenIds] = useState(() => loadSeen(user));
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  const containerRef = useRef(null);
  const slideEls = useRef({});
  const videoEls = useRef({});
  const viewedRef = useRef(new Set());
  const watchRef = useRef({});

  const registerVideo = useCallback((i, el) => { videoEls.current[i] = el; }, []);

  // Seed like/view state from the incoming payload.
  useEffect(() => {
    const l = {}, lc = {}, vc = {}, cc = {};
    promos.forEach(p => { l[p.id] = p.liked || false; lc[p.id] = p.likeCount || 0; vc[p.id] = p.views || 0; cc[p.id] = p.commentCount || 0; });
    setLiked(l); setLikes(lc); setViews(vc); setCommentCounts(cc);
  }, [promos]);

  useEffect(() => { setFollowed(new Set(followingIds)); }, [followingIds]);

  // Signing in mid-feed swaps the anonymous allowance for the user's own.
  useEffect(() => { setSeenIds(loadSeen(user)); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Count the active reel against today's allowance while there is room left.
  useEffect(() => {
    const promo = promos[activeIndex];
    if (!promo) return;
    const id = String(promo.id);
    setSeenIds((prev) => {
      if (prev.includes(id) || prev.length >= cap) return prev;
      const next = [...prev, id];
      saveSeen(user, next);
      return next;
    });
  }, [activeIndex, promos, cap, user]);

  const lockedIds = new Set(promos.filter(p => isReelLocked(p.id, seenIds, cap)).map(p => String(p.id)));

  // Jump to the requested starting reel on open.
  useEffect(() => {
    const el = slideEls.current[startIndex];
    if (el) el.scrollIntoView({ behavior: 'auto' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine the active slide from scroll position via IntersectionObserver.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const obs = new IntersectionObserver((entries) => {
      let best = null;
      entries.forEach((en) => {
        if (en.isIntersecting && (!best || en.intersectionRatio > best.intersectionRatio)) best = en;
      });
      if (best && best.intersectionRatio >= 0.55) {
        const idx = Number(best.target.getAttribute('data-index'));
        if (!Number.isNaN(idx)) setActiveIndex(idx);
      }
    }, { root, threshold: [0.55, 0.8] });
    Object.values(slideEls.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [promos.length]);

  // Play the active video, pause/reset the rest. Fire impression + a one-time
  // view increment when a reel first becomes active.
  useEffect(() => {
    Object.entries(videoEls.current).forEach(([i, el]) => {
      if (!el) return;
      if (Number(i) === activeIndex) {
        el.muted = muted;
        Promise.resolve(el.play())
          .then(() => setAutoplayBlocked(false))
          .catch(() => setAutoplayBlocked(true));
      } else {
        el.pause();
        try { el.currentTime = 0; } catch {}
      }
    });

    const promo = promos[activeIndex];
    if (promo && !viewedRef.current.has(promo.id) && !isReelLocked(promo.id, loadSeen(user), cap)) {
      viewedRef.current.add(promo.id);
      trackFeedEvent('impression', 'promo', promo.id);
      promosAPI.view(promo.id)
        .then((updated) => setViews(prev => ({ ...prev, [promo.id]: updated.views })))
        .catch(() => {});
    }
  }, [activeIndex, muted, promos, user, cap]);

  // Reflect mute changes onto the currently playing element immediately.
  useEffect(() => {
    const el = videoEls.current[activeIndex];
    if (el) el.muted = muted;
  }, [muted, activeIndex]);

  // Watch-depth tracking for the ranking engine (watch_50 / watch_90).
  useEffect(() => {
    const el = videoEls.current[activeIndex];
    const promo = promos[activeIndex];
    if (!el || !promo) return;
    const onTime = () => {
      if (!el.duration) return;
      const frac = el.currentTime / el.duration;
      const seen = watchRef.current[promo.id] || {};
      if (frac >= 0.5 && !seen.w50) {
        watchRef.current[promo.id] = { ...seen, w50: true };
        trackFeedEvent('watch_50', 'promo', promo.id, { watchMs: Math.floor(el.duration * 500) });
      }
      if (frac >= 0.9 && !seen.w90) {
        watchRef.current[promo.id] = { ...watchRef.current[promo.id], w90: true };
        trackFeedEvent('watch_90', 'promo', promo.id, { watchMs: Math.floor(el.duration * 900) });
      }
    };
    el.addEventListener('timeupdate', onTime);
    return () => el.removeEventListener('timeupdate', onTime);
  }, [activeIndex, promos]);

  const scrollToIndex = useCallback((idx) => {
    const el = slideEls.current[idx];
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Keyboard navigation (desktop): arrows to move, Esc to close.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowDown') { e.preventDefault(); scrollToIndex(Math.min(activeIndex + 1, promos.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); scrollToIndex(Math.max(activeIndex - 1, 0)); }
      else if (e.key.toLowerCase() === 'm') setMuted(m => !m);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIndex, promos.length, scrollToIndex, onClose]);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      try { window.localStorage.setItem('gb_promo_muted', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);

  // force=true (double-tap) only ever likes, never unlikes.
  const handleLike = useCallback(async (promo, { force = false } = {}) => {
    if (!user) { onAuthRequired?.(); return; }
    if (force && liked[promo.id]) return;
    try {
      const res = await promosAPI.like(promo.id);
      setLiked(prev => ({ ...prev, [promo.id]: res.liked }));
      setLikes(prev => ({ ...prev, [promo.id]: Math.max(0, (prev[promo.id] || 0) + (res.liked ? 1 : -1)) }));
      if (res.liked) trackFeedEvent('like', 'promo', promo.id);
    } catch {}
  }, [user, liked, onAuthRequired]);

  const handleFollow = useCallback(async (creatorId) => {
    if (!user) { onAuthRequired?.(); return; }
    setFollowed(prev => new Set(prev).add(creatorId)); // optimistic
    try {
      await usersAPI.follow(creatorId);
      onFollowUpdate?.();
    } catch {
      setFollowed(prev => { const n = new Set(prev); n.delete(creatorId); return n; });
    }
  }, [user, onAuthRequired, onFollowUpdate]);

  const dropFromFeed = useCallback((promoId) => {
    const remaining = promos.length - 1;
    onDeleted?.(promoId);
    if (remaining <= 0) onClose?.();
    else setActiveIndex((i) => Math.min(i, remaining - 1));
  }, [promos.length, onDeleted, onClose]);

  const handleDelete = useCallback(async () => {
    if (!deleteFor || deleting) return;
    setDeleting(true);
    try {
      await promosAPI.delete(deleteFor.id);
      const deletedId = deleteFor.id;
      setDeleteFor(null);
      dropFromFeed(deletedId);
    } catch (err) {
      setDeleteFor(null);
      window.alert(err.message || 'Failed to delete this reel');
    } finally {
      setDeleting(false);
    }
  }, [deleteFor, deleting, dropFromFeed]);

  const handleArchive = useCallback(async () => {
    if (!archiveFor || archiving) return;
    setArchiving(true);
    try {
      await promosAPI.archive(archiveFor.id);
      const archivedId = archiveFor.id;
      setArchiveFor(null);
      dropFromFeed(archivedId);
    } catch (err) {
      setArchiveFor(null);
      window.alert(err.message || 'Failed to archive this reel');
    } finally {
      setArchiving(false);
    }
  }, [archiveFor, archiving, dropFromFeed]);

  if (!promos.length) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: '#000' }}>
      {/* Close */}
      <button onClick={onClose} aria-label="Close reels"
        style={{ position: 'absolute', top: 18, left: 16, zIndex: 20, background: 'rgba(0,0,0,0.45)', border: 'none', color: 'white', width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', fontSize: 18, display: 'grid', placeItems: 'center' }}>
        ✕
      </button>

      {/* Desktop-only up/down nav */}
      <button onClick={() => scrollToIndex(Math.max(activeIndex - 1, 0))} aria-label="Previous reel"
        className="gb-reel-nav" style={{ top: 'calc(50% - 52px)' }} disabled={activeIndex === 0}>↑</button>
      <button onClick={() => scrollToIndex(Math.min(activeIndex + 1, promos.length - 1))} aria-label="Next reel"
        className="gb-reel-nav" style={{ top: 'calc(50% + 8px)' }} disabled={activeIndex === promos.length - 1}>↓</button>

      {/* Snap container. Centered phone-width column on desktop, full-bleed on mobile. */}
      <div
        ref={containerRef}
        style={{
          height: '100%', width: '100%', maxWidth: 480, margin: '0 auto',
          overflowY: 'scroll', scrollSnapType: 'y mandatory',
          scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
          display: 'flex', flexDirection: 'column',
        }}
        className="gb-reels-scroll"
      >
        {promos.map((promo, i) => (
          <div
            key={promo.id}
            data-index={i}
            ref={(el) => { slideEls.current[i] = el; }}
            style={{ height: '100%', flex: '0 0 100%', scrollSnapAlign: 'start' }}
          >
            <Slide
              promo={promo}
              index={i}
              activeIndex={activeIndex}
              muted={muted}
              user={user}
              followed={followed.has(promo.user?.id)}
              liked={!!liked[promo.id]}
              likeCount={likes[promo.id] || 0}
              viewCount={views[promo.id] ?? promo.views ?? 0}
              onToggleMute={toggleMute}
              onLike={handleLike}
              onShare={setShareFor}
              onComment={setCommentFor}
              onFollow={handleFollow}
              onPlanTrip={onPlanTrip}
              onDelete={setDeleteFor}
              onArchive={setArchiveFor}
              timeLeft={promo.timeLeft}
              commentCount={commentCounts[promo.id] ?? promo.commentCount ?? 0}
              locked={lockedIds.has(String(promo.id))}
              onUnlock={onAuthRequired}
              autoplayBlocked={autoplayBlocked}
              onPlaybackResumed={() => setAutoplayBlocked(false)}
              registerVideo={registerVideo}
            />
          </div>
        ))}
      </div>

      {shareFor && (
        <PlatformShareModal
          promo={shareFor}
          user={user}
          onClose={() => { trackFeedEvent('share', 'promo', shareFor.id); setShareFor(null); }}
        />
      )}

      {commentFor && (
        <PromoCommentDrawer
          promo={commentFor}
          user={user}
          onAuthRequired={onAuthRequired}
          onClose={() => setCommentFor(null)}
          onCountChange={(id, next) => setCommentCounts(prev => ({ ...prev, [id]: typeof next === 'function' ? next(prev[id] || 0) : next }))}
        />
      )}

      <ConfirmModal
        open={!!deleteFor}
        title="Delete reel?"
        message="This will permanently remove the video. This can't be undone."
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        onConfirm={handleDelete}
        onCancel={() => !deleting && setDeleteFor(null)}
      />

      <ConfirmModal
        open={!!archiveFor}
        title="Archive reel?"
        message="The reel leaves every feed but stays in your posts, so you can restore it later."
        confirmText={archiving ? 'Archiving…' : 'Archive'}
        onConfirm={handleArchive}
        onCancel={() => !archiving && setArchiveFor(null)}
      />

      <style>{`
        .gb-reels-scroll::-webkit-scrollbar{display:none}
        @keyframes gbReelBurst{
          0%{opacity:0;transform:translate(-50%,-50%) scale(0.3)}
          15%{opacity:1;transform:translate(-50%,-50%) scale(1.1)}
          70%{opacity:1;transform:translate(-50%,-50%) scale(1)}
          100%{opacity:0;transform:translate(-50%,-50%) scale(1.3)}
        }
        .gb-reel-nav{position:absolute;right:24px;z-index:20;background:rgba(255,255,255,0.14);
          border:none;color:white;width:44px;height:44px;border-radius:50%;cursor:pointer;
          font-size:20px;font-weight:700;display:grid;place-items:center;transition:background .2s}
        .gb-reel-nav:hover:not(:disabled){background:rgba(255,255,255,0.28)}
        .gb-reel-nav:disabled{opacity:0.3;cursor:default}
        @media(max-width:640px){.gb-reel-nav{display:none}}
      `}</style>
    </div>
  );
}
